/**
 * Per-category how-to + FAQ content — the unique-content registry for the 8
 * unit-converter category pages. Without this, every category page rendered
 * the same template with only the category name swapped in (`m.uc_cat_howto({
 * category })`), which is exactly the thin/doorway-content pattern search
 * engines and AdSense review penalize. Each entry below points at its own
 * message keys with real conversion facts, formulas, and worked examples.
 *
 * Mirrors the `unique()` registry pattern in `src/tools/timer/lib/presets.ts`.
 */
import { m } from "@/paraglide/messages";

export interface CategoryFaq {
  q: () => string;
  a: () => string;
}

export interface CategoryContent {
  howTo: () => string;
  faqs: CategoryFaq[];
}

export const CATEGORY_CONTENT: Record<string, CategoryContent> = {
  length: {
    howTo: () => m.uc_len_howto(),
    faqs: [
      { q: () => m.uc_len_faq1_q(), a: () => m.uc_len_faq1_a() },
      { q: () => m.uc_len_faq2_q(), a: () => m.uc_len_faq2_a() },
      { q: () => m.uc_len_faq3_q(), a: () => m.uc_len_faq3_a() },
      { q: () => m.uc_len_faq4_q(), a: () => m.uc_len_faq4_a() },
    ],
  },
  weight: {
    howTo: () => m.uc_wt_howto(),
    faqs: [
      { q: () => m.uc_wt_faq1_q(), a: () => m.uc_wt_faq1_a() },
      { q: () => m.uc_wt_faq2_q(), a: () => m.uc_wt_faq2_a() },
      { q: () => m.uc_wt_faq3_q(), a: () => m.uc_wt_faq3_a() },
      { q: () => m.uc_wt_faq4_q(), a: () => m.uc_wt_faq4_a() },
    ],
  },
  temperature: {
    howTo: () => m.uc_temp_howto(),
    faqs: [
      { q: () => m.uc_temp_faq1_q(), a: () => m.uc_temp_faq1_a() },
      { q: () => m.uc_temp_faq2_q(), a: () => m.uc_temp_faq2_a() },
      { q: () => m.uc_temp_faq3_q(), a: () => m.uc_temp_faq3_a() },
      { q: () => m.uc_temp_faq4_q(), a: () => m.uc_temp_faq4_a() },
    ],
  },
  volume: {
    howTo: () => m.uc_vol_howto(),
    faqs: [
      { q: () => m.uc_vol_faq1_q(), a: () => m.uc_vol_faq1_a() },
      { q: () => m.uc_vol_faq2_q(), a: () => m.uc_vol_faq2_a() },
      { q: () => m.uc_vol_faq3_q(), a: () => m.uc_vol_faq3_a() },
      { q: () => m.uc_vol_faq4_q(), a: () => m.uc_vol_faq4_a() },
    ],
  },
  area: {
    howTo: () => m.uc_area_howto(),
    faqs: [
      { q: () => m.uc_area_faq1_q(), a: () => m.uc_area_faq1_a() },
      { q: () => m.uc_area_faq2_q(), a: () => m.uc_area_faq2_a() },
      { q: () => m.uc_area_faq3_q(), a: () => m.uc_area_faq3_a() },
      { q: () => m.uc_area_faq4_q(), a: () => m.uc_area_faq4_a() },
    ],
  },
  speed: {
    howTo: () => m.uc_speed_howto(),
    faqs: [
      { q: () => m.uc_speed_faq1_q(), a: () => m.uc_speed_faq1_a() },
      { q: () => m.uc_speed_faq2_q(), a: () => m.uc_speed_faq2_a() },
      { q: () => m.uc_speed_faq3_q(), a: () => m.uc_speed_faq3_a() },
      { q: () => m.uc_speed_faq4_q(), a: () => m.uc_speed_faq4_a() },
    ],
  },
  data: {
    howTo: () => m.uc_data_howto(),
    faqs: [
      { q: () => m.uc_data_faq1_q(), a: () => m.uc_data_faq1_a() },
      { q: () => m.uc_data_faq2_q(), a: () => m.uc_data_faq2_a() },
      { q: () => m.uc_data_faq3_q(), a: () => m.uc_data_faq3_a() },
      { q: () => m.uc_data_faq4_q(), a: () => m.uc_data_faq4_a() },
    ],
  },
  time: {
    howTo: () => m.uc_time_howto(),
    faqs: [
      { q: () => m.uc_time_faq1_q(), a: () => m.uc_time_faq1_a() },
      { q: () => m.uc_time_faq2_q(), a: () => m.uc_time_faq2_a() },
      { q: () => m.uc_time_faq3_q(), a: () => m.uc_time_faq3_a() },
      { q: () => m.uc_time_faq4_q(), a: () => m.uc_time_faq4_a() },
    ],
  },
};

/** Safe lookup (`Object.hasOwn` so inherited keys don't resolve unexpectedly). */
export function getCategoryContent(id: string): CategoryContent | undefined {
  return Object.hasOwn(CATEGORY_CONTENT, id) ? CATEGORY_CONTENT[id] : undefined;
}
